'use client';

/**
 * ChatWindow Component
 *
 * REQUIREMENTS SUMMARY:
 * - Main chat interface with message list, input, and conversation history
 * - Role-specific greetings: staff keeps "Hi [Name]!" / "Hi there!"; parent gets "Hello!" when no name;
 *   athlete gets "Hey [Name]!" / "Hey there!" for a more casual tone
 * - Role-specific intro text and suggestion prompts (staff, parent, athlete)
 */

import React, { useRef, useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import { cn } from '@/utils/common/helpers';
import { ChatMessage, Conversation, type ChatDraft } from '@/hooks/chat/useChat';
import { useIsMobile } from '@/hooks/ui/useMediaQuery';
import { MessageList } from './MessageList';
import { ChatInput } from './ChatInput';
import { QuickCommandsMenu } from './QuickCommandsMenu';
import AssistantRobotIcon from './AssistantRobotIcon';
import { OrgRole } from '@/utils/constants/roles';

interface ChatWindowProps {
  isOpen: boolean;
  messages: ChatMessage[];
  conversations: Conversation[];
  currentConversationId: string | null;
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  userFirstName?: string;
  userType?: 'staff' | 'parent' | 'athlete' | 'unknown';
  userRole?: OrgRole | null;
  onSendMessage: (message: string, quickCommand?: string, files?: File[]) => Promise<boolean | void>;
  draftKey?: number;
  /** Text to put in the input box without sending it. */
  draft?: ChatDraft | null;
  /** The draft was sent or edited. */
  onDraftDone?: (id: number) => void;
  onRetrySupportAttachments?: (messageId: string) => Promise<void>;
  loadAttachment?: (id: string) => Promise<Blob>;
  onStopGeneration?: () => void;
  onNewConversation: () => void;
  onLoadConversation: (id: string) => Promise<void>;
  onDeleteConversation: (id: string) => Promise<void>;
  onClose: () => void;
  onClearError: () => void;
  onFeedback?: (messageId: string, feedback: 'positive' | 'negative', comment?: string) => void;
  onConfirmNavigation?: (messageId: string, confirm: boolean) => void;
  isMobile?: boolean;
  suggestions?: string[];
  organizationName?: string;
}

/**
 * Get role-based suggestion prompts based on user type and role
 */
function getSuggestionPrompts(
  userType?: 'staff' | 'parent' | 'athlete' | 'unknown',
  userRole?: OrgRole | null
): string[] {
  // Staff suggestions based on specific role
  if (userType === 'staff') {
    // Admin/Owner - full management access including announcements
    if (userRole === 'owner' || userRole === 'administrator') {
      return [
        "Show me today's schedule",
        "Which athletes have overdue payments?",
        "Send a reminder to Senior All Stars about practice",
      ];
    }
    // Head Coach/Coach - team focused with attendance actions
    if (userRole === 'head_coach' || userRole === 'coach' || userRole === 'assistant_coach') {
      return [
        "What's on my team's schedule this week?",
        "Show athletes on my teams",
        "Mark Sarah as present for today's practice",
      ];
    }
    // Front desk - operations focused
    if (userRole === 'front_desk') {
      return [
        "Show me today's schedule",
        "Show upcoming events",
        "Send a reminder about the holiday closure",
      ];
    }
    // Default staff suggestions
    return [
      "Show me today's schedule",
      "Show upcoming events",
      "Send a reminder to parents",
    ];
  }

  // Parent suggestions - child-focused with RSVP action
  if (userType === 'parent') {
    return [
      "What's on my athlete's schedule this week?",
      "Check my payment status",
      "RSVP yes for the Holiday Showcase",
    ];
  }

  // Athlete suggestions - personal schedule with RSVP action
  if (userType === 'athlete') {
    return [
      "What's on my schedule this week?",
      "Show my upcoming competitions",
      "RSVP yes for the showcase event",
    ];
  }

  // Default/unknown - generic suggestions
  return [
    "What's on my schedule this week?",
    "Show me upcoming competitions",
    "Check my payment status",
  ];
}

export function ChatWindow({
  isOpen,
  messages,
  conversations,
  currentConversationId,
  isLoading,
  isSending,
  error,
  userFirstName,
  userType,
  userRole,
  onSendMessage,
  draftKey,
  draft,
  onDraftDone,
  loadAttachment,
  onRetrySupportAttachments,
  onStopGeneration,
  onNewConversation,
  onLoadConversation,
  onDeleteConversation,
  onClose,
  onClearError,
  onFeedback,
  onConfirmNavigation,
  isMobile: isMobileProp,
  suggestions: dynamicSuggestions,
  organizationName,
}: ChatWindowProps) {
  const [showHistory, setShowHistory] = useState(false);
  const [showQuickCommands, setShowQuickCommands] = useState(false);
  const isMobileHook = useIsMobile();
  const isMobile = isMobileProp ?? isMobileHook;
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Resizing state
  const [size, setSize] = useState({ width: 500, height: 600 });
  const [isResizing, setIsResizing] = useState(false);

  // Dragging state
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const resizeAnchorRef = useRef({ right: 0, bottom: 0 });

  // Load saved size and position from localStorage
  useEffect(() => {
    if (!isMobile) {
      let currentWidth = 500;
      let currentHeight = 600;

      const savedSize = localStorage.getItem('chat-assistant-size');
      if (savedSize) {
        try {
          const parsed = JSON.parse(savedSize);
          currentWidth = Math.max(320, Math.min(parsed.width, window.innerWidth - 48));
          currentHeight = Math.max(400, Math.min(parsed.height, window.innerHeight - 128));
        } catch (e) {
          // Ignore parse errors
        }
      }
      setSize({ width: currentWidth, height: currentHeight });

      const savedPosition = localStorage.getItem('chat-assistant-position');
      if (savedPosition) {
        try {
          const parsed = JSON.parse(savedPosition);
          setPosition({
            x: Math.max(0, Math.min(parsed.x, window.innerWidth - 100)),
            y: Math.max(0, Math.min(parsed.y, window.innerHeight - 100))
          });
        } catch (e) {
          // Default to bottom-right
          setPosition({
            x: window.innerWidth - currentWidth - 24,
            y: window.innerHeight - currentHeight - 96
          });
        }
      } else {
        // Default: bottom-right position (matches original sm:bottom-24 sm:right-6)
        setPosition({
          x: window.innerWidth - currentWidth - 24,
          y: window.innerHeight - currentHeight - 96
        });
      }
    }
  }, [isMobile]);

  // Handle resizing logic (top-left handle, bottom-right corner stays fixed)
  useEffect(() => {
    if (!isResizing || isMobile) return;

    const handleMouseMove = (e: MouseEvent) => {
      const { right, bottom } = resizeAnchorRef.current;
      const newWidth = Math.max(320, right - e.clientX);
      const newHeight = Math.max(400, bottom - e.clientY);
      setSize({ width: newWidth, height: newHeight });
      setPosition({
        x: Math.max(0, right - newWidth),
        y: Math.max(0, bottom - newHeight)
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setSize(s => {
        localStorage.setItem('chat-assistant-size', JSON.stringify(s));
        return s;
      });
      setPosition(p => {
        if (p) localStorage.setItem('chat-assistant-position', JSON.stringify(p));
        return p;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, isMobile]);

  // Handle dragging logic
  useEffect(() => {
    if (!isDragging || isMobile) return;

    document.body.style.cursor = 'grabbing';

    const handleMouseMove = (e: MouseEvent) => {
      const newX = e.clientX - dragOffsetRef.current.x;
      const newY = e.clientY - dragOffsetRef.current.y;
      setPosition({
        x: Math.max(0, Math.min(newX, window.innerWidth - size.width)),
        y: Math.max(0, Math.min(newY, window.innerHeight - size.height))
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setPosition(p => {
        if (p) localStorage.setItem('chat-assistant-position', JSON.stringify(p));
        return p;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isMobile, size.width, size.height]);
  
  // Get role-specific greeting. Staff keeps current; parent/athlete get distinct welcomes.
  const greeting = useMemo(() => {
    if (userType === 'staff') {
      return userFirstName ? `Hi ${userFirstName}!` : 'Hi there!';
    }
    if (userType === 'parent') {
      return userFirstName ? `Hi ${userFirstName}!` : 'Hello!';
    }
    if (userType === 'athlete') {
      return userFirstName ? `Hey ${userFirstName}!` : 'Hey there!';
    }
    return userFirstName ? `Hi ${userFirstName}!` : 'Hi there!';
  }, [userType, userFirstName]);
  
  // Get role-based suggestion prompts (fallback if dynamic ones aren't available yet)
  const roleDefaults = useMemo(
    () => getSuggestionPrompts(userType, userRole),
    [userType, userRole]
  );

  // Use dynamic suggestions if provided and not empty, otherwise fallback to defaults
  const suggestionPrompts = dynamicSuggestions && dynamicSuggestions.length > 0 
    ? dynamicSuggestions 
    : roleDefaults;

  // Build contextual intro text
  const introText = useMemo(() => {
    let mainIntro = "I'm great for assisting with communications and can answer any questions regarding ";
    
    if (userType === 'staff') {
      mainIntro = "I'm great for assisting with team communications, staff tasks, and answering any questions regarding ";
    } else if (userType === 'parent') {
      mainIntro = "I'm great for helping with your schedule, competition travel info, communications from coaches, and any questions regarding ";
    } else if (userType === 'athlete') {
      mainIntro = "I'm great for assisting with your schedule, team updates, and any questions regarding ";
    }

    return `I'm your CheerVue assistant. ${mainIntro}${organizationName || 'your gym'}. I can also help with other tasks, and new abilities are added often!`;
  }, [userType, organizationName]);
  
  // Scroll to bottom when new messages arrive
  useEffect(() => {
    // Only auto-scroll if we have messages and we're not currently loading history
    if (messages.length > 0 && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);
  
  // Clamp panel back into view on browser resize / zoom change
  useEffect(() => {
    if (isMobile) return;

    const handleResize = () => {
      setPosition((prev) => {
        if (!prev) return prev;
        const maxX = window.innerWidth - Math.min(size.width, window.innerWidth - 48);
        const maxY = window.innerHeight - Math.min(size.height, window.innerHeight - 128);
        const clampedX = Math.max(0, Math.min(prev.x, maxX));
        const clampedY = Math.max(0, Math.min(prev.y, maxY));
        if (clampedX === prev.x && clampedY === prev.y) return prev;
        const next = { x: clampedX, y: clampedY };
        localStorage.setItem('chat-assistant-position', JSON.stringify(next));
        return next;
      });

      setSize((prev) => {
        const clampedW = Math.min(prev.width, window.innerWidth - 48);
        const clampedH = Math.min(prev.height, window.innerHeight - 128);
        if (clampedW === prev.width && clampedH === prev.height) return prev;
        const next = { width: Math.max(320, clampedW), height: Math.max(400, clampedH) };
        localStorage.setItem('chat-assistant-size', JSON.stringify(next));
        return next;
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile, size.width, size.height]);

  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !document.querySelector('.modal')) {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  
  // Animation variants
  const variants = useMemo(() => ({
    initial: isMobile ? { opacity: 0, y: '100%' } : { opacity: 0, y: 20, scale: 0.95 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: isMobile ? { opacity: 0, y: '100%' } : { opacity: 0, y: 20, scale: 0.95 },
  }), [isMobile]);
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="chat-window"
          variants={variants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className={cn(
            'fixed z-999999 overflow-hidden bg-white dark:bg-gray-900 flex flex-col',
            // Mobile: Full screen
            'inset-0 w-full h-full rounded-none border-0',
            // Desktop (sm and up): Floating window
            'sm:inset-auto',
            !position && 'sm:bottom-24 sm:right-6',
            'sm:max-w-[calc(100vw-3rem)]',
            'sm:max-h-[calc(100vh-8rem)]',
            'sm:rounded-2xl sm:border sm:border-gray-200 sm:dark:border-gray-700 sm:shadow-2xl',
            (isResizing || isDragging) && 'select-none'
          )}
          style={{
            width: isMobile ? '100%' : `${size.width}px`,
            height: isMobile ? '100%' : `${size.height}px`,
            ...(!isMobile && position ? { left: `${position.x}px`, top: `${position.y}px` } : {}),
          }}
        >
          {/* Resize handle (top-left) - only on desktop */}
          {!isMobile && (
            <div
              className="absolute top-1 left-1 w-6 h-6 cursor-nwse-resize z-[100] group flex items-start justify-start"
              onMouseDown={(e) => {
                e.preventDefault();
                // Capture bottom-right anchor (stays fixed during resize)
                if (position) {
                  resizeAnchorRef.current = {
                    right: position.x + size.width,
                    bottom: position.y + size.height
                  };
                }
                setIsResizing(true);
              }}
            >
              <div className="opacity-100 transition-opacity">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-gray-500 dark:text-gray-400 group-hover:text-brand-500"
                >
                  <circle cx="2" cy="2" r="1.2" fill="currentColor" />
                  <circle cx="6" cy="2" r="1.2" fill="currentColor" />
                  <circle cx="10" cy="2" r="1.2" fill="currentColor" />
                  <circle cx="2" cy="6" r="1.2" fill="currentColor" />
                  <circle cx="6" cy="6" r="1.2" fill="currentColor" />
                  <circle cx="2" cy="10" r="1.2" fill="currentColor" />
                </svg>
              </div>
            </div>
          )}

          {/* Header */}
          <div
            className={cn(
              "flex items-center justify-between px-4 py-4 sm:py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800",
              !isMobile && !isDragging && 'sm:cursor-grab',
              isDragging && 'cursor-grabbing'
            )}
            onMouseDown={(e) => {
              if (isMobile || !position) return;
              if ((e.target as HTMLElement).closest('button')) return;
              e.preventDefault();
              dragOffsetRef.current = {
                x: e.clientX - position.x,
                y: e.clientY - position.y
              };
              setIsDragging(true);
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-8 sm:h-8 shrink-0 rounded-full bg-gradient-to-r from-brand-600 to-purple-600 flex items-center justify-center">
                <AssistantRobotIcon className="w-[30px] h-[22px] sm:w-6 sm:h-[18px] text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white text-base sm:text-sm">
                  CheerVue Assistant
                </h3>
                <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400">
                  {isSending ? 'Thinking...' : 'Ask questions or help with tasks'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-1">
              {/* History toggle */}
              <button
                onClick={() => setShowHistory(!showHistory)}
                className={cn(
                  'p-2 rounded-lg transition-colors flex items-center justify-center',
                  showHistory
                    ? 'bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400'
                )}
                title="Conversation history"
              >
                <GoogleMaterialIcon name="history" size={isMobile ? 'md' : 'sm'} />
              </button>
              
              {/* New conversation */}
              <button
                onClick={onNewConversation}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors flex items-center justify-center"
                title="New conversation"
              >
                <GoogleMaterialIcon name="add_comment" size={isMobile ? 'md' : 'sm'} />
              </button>
              
              {/* Close button */}
              <button
                onClick={onClose}
                className="p-3 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors flex items-center justify-center"
                title="Close"
              >
                <GoogleMaterialIcon name="close" size={isMobile ? 'md' : 'sm'} />
              </button>
            </div>
          </div>
          
          {/* Error banner */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 px-4 py-2 flex items-center justify-between"
              >
                <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                <button
                  onClick={onClearError}
                  className="text-red-500 hover:text-red-700 dark:hover:text-red-300"
                >
                  <GoogleMaterialIcon name="close" size="xs" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Content area */}
          <div className="flex-1 flex overflow-hidden relative min-h-0">
            {/* History sidebar */}
            {/* History sidebar overlay for mobile */}
            <AnimatePresence>
              {showHistory && isMobile && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowHistory(false)}
                  className="absolute inset-0 bg-black/20 backdrop-blur-sm z-10 sm:hidden"
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showHistory && (
                <motion.div
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: 'var(--history-width)', opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  className={cn(
                    'border-r border-gray-200 dark:border-gray-700 overflow-hidden z-20',
                    'bg-white dark:bg-gray-900',
                    'absolute inset-y-0 left-0 sm:relative sm:inset-auto',
                    '[--history-width:240px] sm:[--history-width:180px]'
                  )}
                >
                  <div className="h-full overflow-y-auto p-2">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 px-2 py-1 uppercase tracking-wider">
                      Recent
                    </p>
                    {isLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="w-5 h-5 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : conversations.length === 0 ? (
                      <p className="text-xs text-gray-400 dark:text-gray-500 px-2 py-2">
                        No conversations yet
                      </p>
                    ) : (
                      <div className="space-y-1">
                        {conversations.map((conv) => (
                          <div
                            key={conv.id}
                            className={cn(
                              'group relative w-full text-left px-2 py-1.5 rounded-lg text-xs transition-colors cursor-pointer',
                              'hover:bg-gray-100 dark:hover:bg-gray-800',
                              conv.id === currentConversationId
                                ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300'
                                : 'text-gray-700 dark:text-gray-300'
                            )}
                            onClick={() => {
                              onLoadConversation(conv.id);
                              if (isMobile) setShowHistory(false);
                            }}
                          >
                            <p className="truncate font-medium pr-5">{conv.title}</p>
                            <p className="text-gray-400 dark:text-gray-500 truncate">
                              {new Date(conv.last_message_at).toLocaleDateString()}
                            </p>
                            {/* Delete button - visible on hover */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteConversation(conv.id);
                              }}
                              className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-all"
                              title="Delete conversation"
                            >
                              <GoogleMaterialIcon name="delete" size="xs" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Messages area */}
            <div className="flex-1 flex flex-col overflow-hidden min-h-0">
              <div className="flex-1 overflow-y-auto p-4 overscroll-contain">
                <div className="min-h-[calc(100%+1px)] flex flex-col">
                  {messages.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
                      <div className="w-16 h-16 rounded-full bg-brand-100 dark:bg-brand-900/30 flex items-center justify-center mb-4">
                        <GoogleMaterialIcon
                          name="waving_hand"
                          size="lg"
                          className="text-brand-600 dark:text-brand-400"
                        />
                      </div>
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                        {greeting}
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                        {introText}
                      </p>
                      <div className="flex flex-col gap-2 items-center w-full max-w-[280px]">
                        <button
                          onClick={() => onSendMessage("Suggest a Feature", "Suggest a Feature")}
                          disabled={isSending}
                          className="w-full text-center text-sm px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all border border-gray-100 dark:border-gray-700 shadow-sm font-semibold active:scale-95 disabled:opacity-50"
                        >
                          Suggest a Feature
                        </button>
                        <div className="flex items-center gap-4">
                          <button
                            onClick={() => onSendMessage("Report a Bug", "Report a Bug")}
                            disabled={isSending}
                            className="text-xs text-gray-400 dark:text-gray-500 hover:text-brand-600 dark:hover:text-brand-400 underline transition-colors disabled:opacity-50"
                          >
                            Report a Bug
                          </button>
                          <button
                            onClick={() => onSendMessage("Share Feedback", "Share Feedback")}
                            disabled={isSending}
                            className="text-xs text-gray-400 dark:text-gray-500 hover:text-brand-600 dark:hover:text-brand-400 underline transition-colors disabled:opacity-50"
                          >
                            Share Feedback
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <MessageList
                      loadAttachment={loadAttachment}
                      onRetrySupportAttachments={onRetrySupportAttachments}
                      messages={messages} 
                      onFeedback={onFeedback} 
                      onConfirmNavigation={onConfirmNavigation}
                    />
                  )}
                </div>
                <div ref={messagesEndRef} />
              </div>
              
              {/* Input */}
              <div className="relative">
                <ChatInput
                  draftKey={draftKey}
                  draft={draft}
                  onDraftDone={onDraftDone}
                  onSend={onSendMessage}
                  onStop={isSending ? onStopGeneration : undefined}
                  disabled={isSending || isLoading}
                  placeholder={isSending ? 'Thinking...' : 'Type a message...'}
                  showKeyboardHints={!isMobile}
                  footer={messages.length > 0 ? (
                    <FeedbackLinks onSendMessage={onSendMessage} disabled={isSending} />
                  ) : undefined}
                  onOpenQuickCommands={() => setShowQuickCommands(true)}
                />
                
                {/* Quick Commands Menu */}
                <AnimatePresence>
                  {showQuickCommands && (
                    <QuickCommandsMenu
                      isOpen={showQuickCommands}
                      onClose={() => setShowQuickCommands(false)}
                      onUseCommand={(commandText, commandLabel) => {
                        onSendMessage(commandText, commandLabel);
                      }}
                      disabled={isSending}
                    />
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default ChatWindow;

const FEEDBACK_LINKS = ['Suggest a Feature', 'Report a Bug', 'Share Feedback'] as const;

function FeedbackLinks({
  onSendMessage,
  disabled,
}: {
  onSendMessage: (message: string, quickCommand?: string) => unknown;
  disabled: boolean;
}) {
  return (
    <div className="flex items-center justify-center gap-1.5 mt-3 text-[11px] text-gray-400 dark:text-gray-500">
      {FEEDBACK_LINKS.map((label, i) => (
        <React.Fragment key={label}>
          {i > 0 && <span aria-hidden="true">·</span>}
          <button
            type="button"
            onClick={() => onSendMessage(label, label)}
            disabled={disabled}
            className="underline hover:text-brand-600 dark:hover:text-brand-400 transition-colors disabled:opacity-50"
          >
            {label}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
}
